import {ConfirmationDialog, dateTime, FormDialog} from '@management-ui/core';
import {Add, Check, Delete, Download, Send, Visibility} from '@mui/icons-material';
import {IconButton, Tooltip} from '@mui/material';
import * as React from 'react';
import {useCallback, useContext, useState} from 'react';
import AccountFormStatus from '../../../../components/AccountFormStatus';
import {ServiceContext} from '../../../../components/Services';
import Table from '../../../../components/Table';
import SendForm from '../../forms/SendForm';

const AccountForms = ({contact, onContactUpdated}) => {
  const services = useContext(ServiceContext);
  const [showNew, setShowNew] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleNew = useCallback((confirmed) => {
    setShowNew(false);
    if (confirmed) {
      setLoading(true);
      services.contact.accountForm.create(contact).then(updated => {
        setLoading(false);
        onContactUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [services, contact, onContactUpdated]);

  const handleSend = useCallback(async (name, email) => new Promise((resolve, reject) => {
    setLoading(true);
    services.contact.accountForm.send(contact, selected, name, email).then(updated => {
      setLoading(false);
      onContactUpdated(updated);
      setShowSend(false);
      resolve();
    }).catch(() => {
      setLoading(false);
      setShowSend(false);
      reject();
    });
  }), [services, contact, selected, onContactUpdated]);

  const handleDownload = useCallback((form) => {
    setLoading(true);
    services.contact.accountForm.download(contact, form)
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [services, contact]);

  const handleComplete = useCallback((confirmed) => {
    setShowComplete(false);
    if (confirmed) {
      setLoading(true);
      services.contact.accountForm.complete(contact, selected).then(updated => {
        setLoading(false);
        onContactUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [services, contact, selected, onContactUpdated]);

  const handleDelete = useCallback((confirmed) => {
    setShowDelete(false);
    if (confirmed) {
      setLoading(true);
      services.contact.accountForm.delete(contact, selected).then(updated => {
        setLoading(false);
        onContactUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [services, contact, selected, onContactUpdated]);

  return (
    <>
      <Table className={loading ? 'loading' : ''}>
        <Table.Actions>
          <h3>Account Forms</h3>
          <Tooltip title="Create New Account Form">
            <span>
              <IconButton
                onClick={() => setShowNew(true)}
                disabled={contact.archived}>
                <Add/>
              </IconButton>
            </span>
          </Tooltip>
        </Table.Actions>
        <Table.Row>
          <Table.Header>Status</Table.Header>
          <Table.Header>Sent At</Table.Header>
          <Table.Header>Completed At</Table.Header>
          <Table.Header>Completed By</Table.Header>
          <Table.Action/>
          <Table.Action/>
          <Table.Action/>
          <Table.Action/>
          <Table.Action/>
        </Table.Row>
        {contact.account_forms.length > 0 ? (
          contact.account_forms.map((form, index) => (
            <Table.Row key={index}>
              <Table.Column><AccountFormStatus form={form}/></Table.Column>
              <Table.Column>{form.sent_at ? dateTime(form.sent_at) : '-'}</Table.Column>
              <Table.Column>{form.completed_at ? dateTime(form.completed_at) : '-'}</Table.Column>
              <Table.Column>{form.completed_by ?? '-'}</Table.Column>
              <Table.Action>
                <Tooltip title="View Account Form">
                  <span>
                    <IconButton onClick={() => window.open(form.link, '_blank')}>
                      <Visibility/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Send Account Form">
                  <span>
                    <IconButton onClick={() => setSelected(form) || setShowSend(true)}>
                      <Send/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Complete Account Form">
                  <span>
                    <IconButton
                      onClick={() => setSelected(form) || setShowComplete(true)}
                      disabled={form.status === 'completed'}>
                      <Check/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Download Account Form">
                  <span>
                    <IconButton
                      onClick={() => handleDownload(form)}
                      disabled={form.status !== 'completed' || index > 0}>
                      <Download/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
              <Table.Action>
                <Tooltip title="Delete Account Form">
                  <span>
                    <IconButton
                      onClick={() => setSelected(form) || setShowDelete(true)}
                      disabled={form.status !== 'created'}>
                      <Delete/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Table.Action>
            </Table.Row>
          ))
        ) : (
          <Table.NoRecords>There are no account forms for this contact</Table.NoRecords>
        )}
      </Table>
      <ConfirmationDialog
        title="Create New Account Form"
        message="Would you like to create new account form?"
        open={showNew}
        onClose={handleNew}
      />
      <FormDialog
        open={showSend}
        title="Who would you like to send this account form to?"
        maxWidth="sm"
        onClose={() => setShowSend(false)}
        render={(props) => (
          <SendForm
            {...props}
            entity={{}}
            onSave={({name, email}) => handleSend(name, email)}/>
        )}
      />
      <ConfirmationDialog
        title="Complete Account Form"
        message="Are you sure you want to mark this account form as complete?"
        open={showComplete}
        onClose={handleComplete}
      />
      <ConfirmationDialog
        title="Delete Account Form"
        message="Are you sure you want to delete this account form?"
        open={showDelete}
        onClose={handleDelete}
      />
    </>
  );
};

export default AccountForms;
